// src/modules/Login/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import config from '../../config/config';
import { msalInstance } from '../../config/authConfig';

const apiUrl = config.apiBaseUrl;
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => sessionStorage.getItem('isAuthenticated') === 'true'
  );

  useEffect(() => {
    sessionStorage.setItem('isAuthenticated', isAuthenticated);
  }, [isAuthenticated]);

  // ─── Login con usuario y contraseña ────────────────────────────────────────
  const login = async (username, password) => {
    const cleanUser = String(username || '').trim().toLowerCase();
    let deviceToken = (cleanUser && localStorage.getItem('eseasa_device_token_' + cleanUser)) || localStorage.getItem('eseasa_device_token') || null;
    if (!deviceToken && typeof document !== 'undefined') {
      const match = document.cookie.match(/(?:^|; )eseasa_device_token=([^;]*)/);
      if (match) deviceToken = decodeURIComponent(match[1]);
    }

    const response = await fetch(`${apiUrl}/api/login`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(deviceToken ? { 'x-device-token': deviceToken } : {})
      },
      body: JSON.stringify({ username, password, deviceToken }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || data.mensaje || 'Credenciales incorrectas');
    }

    // Si requiere 2FA (código por correo)
    if (data.require2fa) {
      return data;
    }

    let { permisos = [], perfilID, perfilNombre, usuarioID, empresas = [], correo, accesos = {}, rutaInicial } = data;

    let permisosNormalizados = normalizePermisos(permisos);
    if (permisosNormalizados.includes(1) && !permisosNormalizados.includes(16)) {
      permisosNormalizados.push(16);
    }

    guardarSesion({ permisos: permisosNormalizados, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername: username });
    setIsAuthenticated(true);

    return { ...data, rutaInicial: rutaInicial || '/Solicitudes', permisos: permisosNormalizados };
  };

  // ─── Verificar Código 2FA ──────────────────────────────────────────────────
  const verify2fa = async (tempToken, codigo, username, confiarDispositivo = false) => {
    const response = await fetch(`${apiUrl}/api/login/verificar-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tempToken, codigo, username, confiarDispositivo }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || data.mensaje || 'Código inválido o expirado');
    }

    // Si el backend otorgó un token para recordar este dispositivo por 7 días
    if (data.deviceToken) {
      localStorage.setItem('eseasa_device_token', data.deviceToken);
      if (username) localStorage.setItem('eseasa_device_token_' + String(username).trim().toLowerCase(), data.deviceToken);
      try {
        document.cookie = `eseasa_device_token=${encodeURIComponent(data.deviceToken)}; max-age=604800; path=/; SameSite=Lax`;
      } catch (_) {}
    }

    let { permisos = [], perfilID, perfilNombre, usuarioID, empresas = [], correo, loggedInUsername, accesos = {}, rutaInicial } = data;

    let permisosNormalizados = normalizePermisos(permisos);
    if (permisosNormalizados.includes(1) && !permisosNormalizados.includes(16)) {
      permisosNormalizados.push(16);
    }

    guardarSesion({ permisos: permisosNormalizados, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername: loggedInUsername || username || '' });
    setIsAuthenticated(true);

    return { ...data, rutaInicial: rutaInicial || '/Solicitudes', permisos: permisosNormalizados };
  };

  // ─── Reenviar Código 2FA ───────────────────────────────────────────────────
  const resend2fa = async (tempToken, username) => {
    const response = await fetch(`${apiUrl}/api/login/reenviar-2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tempToken, username }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || data.mensaje || 'Error al reenviar código');
    }

    return data;
  };

  // ─── Login con Microsoft ──────────────────────────────────────────────────
  const loginWithMicrosoft = async (email, name) => {
    const response = await fetch(`${apiUrl}/api/auth/microsoft-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.mensaje || 'No se pudo autenticar con Microsoft');
    }

    const userData = await response.json();

    let { permisos = [], perfilID, perfilNombre, usuarioID, empresas = [], correo, loggedInUsername, accesos = {}, rutaInicial } = userData;

    let permisosNormalizados = normalizePermisos(permisos);
    if (permisosNormalizados.includes(1) && !permisosNormalizados.includes(16)) {
      permisosNormalizados.push(16);
    }

    guardarSesion({ permisos: permisosNormalizados, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername: loggedInUsername || email });
    setIsAuthenticated(true);

    return { ...userData, rutaInicial: rutaInicial || '/Solicitudes', permisos: permisosNormalizados };
  };

  // ─── Notificar Bloqueo de Pantalla por Inactividad ─────────────────────────
  const lockSession = async () => {
    try {
      const username = sessionStorage.getItem('loggedInUsername') || '';
      const usuarioId = sessionStorage.getItem('usuarioID') || null;
      await fetch(`${apiUrl}/api/login/bloquear-sesion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, usuarioId }),
      }).catch(() => {
        return fetch(`${apiUrl}/api/auth/bloquear-sesion`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, usuarioId }),
        });
      });
    } catch (e) {
      // No bloquea la UI si falla la red
    }
  };

  // ─── Desbloquear Pantalla con Contraseña (Conserva Estado/Filtros) ──────────
  const unlockSession = async (password) => {
    const username = sessionStorage.getItem('loggedInUsername') || '';
    let usuarioId = null;
    let correo = '';
    try {
      usuarioId = JSON.parse(sessionStorage.getItem('UsuarioId'));
    } catch (e) {}
    try {
      correo = JSON.parse(sessionStorage.getItem('correo'));
    } catch (e) {}

    if (!username && !correo && !usuarioId) {
      throw new Error('No hay una sesión activa para desbloquear');
    }

    let response = await fetch(`${apiUrl}/api/login/desbloquear-sesion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, usuarioId, correo, password }),
    }).catch(() => null);

    if (!response || !response.ok) {
      const alt = await fetch(`${apiUrl}/api/auth/desbloquear-sesion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, usuarioId, correo, password }),
      }).catch(() => null);
      if (alt) response = alt;
    }

    const data = await (response ? response.json().catch(() => ({})) : {});

    if (!response || !response.ok || !data.success) {
      if (response && response.status === 404) {
        throw new Error('El servicio de desbloqueo no está activo en este servidor. Por favor recarga o vuelve a iniciar sesión.');
      }
      throw new Error(data?.message || 'Contraseña incorrecta');
    }

    return data;
  };

  // ─── Logout Limpio y Total ─────────────────────────────────────────────────
  const logout = () => {
    try {
      const username = sessionStorage.getItem('loggedInUsername') || '';
      const usuarioId = sessionStorage.getItem('usuarioID') || null;
      fetch(`${apiUrl}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, usuarioId }),
      }).catch(() => {});
    } catch (e) {}

    setIsAuthenticated(false);
    sessionStorage.clear();
    try {
      const accounts = msalInstance.getAllAccounts();
      accounts.forEach((acc) => {
        try {
          msalInstance.getTokenCache().removeAccount(acc);
        } catch (e) {}
      });
      msalInstance.setActiveAccount(null);
    } catch (e) {
      console.warn('Error clearing MSAL cache:', e);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, verify2fa, resend2fa, loginWithMicrosoft, lockSession, unlockSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// ─── Helpers ─────────────────────────────────────────────────────────────────
function normalizePermisos(permisos) {
  if (Array.isArray(permisos)) return permisos.map(Number).filter(n => !isNaN(n));
  if (typeof permisos === 'string') return permisos.split(',').map(p => Number(p.trim())).filter(n => !isNaN(n));
  if (typeof permisos === 'number') return [permisos];
  return [];
}

function guardarSesion({ permisos, perfilID, perfilNombre, usuarioID, empresas, correo, accesos, rutaInicial, loggedInUsername }) {
  sessionStorage.setItem('isAuthenticated', 'true');
  sessionStorage.setItem('Permisos', JSON.stringify(permisos));
  sessionStorage.setItem('PerfilID', JSON.stringify(perfilID || null));
  sessionStorage.setItem('PerfilNombre', JSON.stringify(perfilNombre || ''));
  sessionStorage.setItem('UsuarioId', JSON.stringify(usuarioID || null));
  sessionStorage.setItem('loggedInUsername', loggedInUsername || '');
  sessionStorage.setItem('correo', JSON.stringify(correo || ''));
  sessionStorage.setItem('RutaInicial', rutaInicial || '/Solicitudes');
  if (Array.isArray(empresas) && empresas.length > 0) {
    sessionStorage.setItem('EmpresaData', JSON.stringify(empresas));
  } else {
    sessionStorage.removeItem('EmpresaData');
  }
  if (accesos && typeof accesos === 'object') {
    sessionStorage.setItem('Accesos', JSON.stringify(accesos));
  } else {
    sessionStorage.removeItem('Accesos');
  }
}
